import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  Search,
  Bell,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  ChevronRight,
  FileText,
  Archive,
  Loader2
} from 'lucide-react';
import DashboardSwitcher from '../components/layout/DashboardSwitcher';
import OwnerTopNav from '../components/layout/OwnerTopNav';
import Sidebar from '../components/layout/Sidebar';
import { ownerService } from '../services/owner.service';
import { buildPublicFileUrl } from '../utils/fileUrl';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=400';

const buildImageUrl = (imageUrl) => {
  if (!imageUrl) return FALLBACK_IMAGE;
  return buildPublicFileUrl(imageUrl);
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return 'RF 0';
  }

  return `RF ${Math.round(Number(amount)).toLocaleString()}`;
};

const OwnerBookings = () => {
  const firstName = localStorage.getItem('firstName') || '';
  const lastName = localStorage.getItem('lastName') || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'Owner';
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.trim() || `${fullName[0] || 'O'}`;

  const [activeTab, setActiveTab] = useState('Pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await ownerService.getOwnerBookings();
        setBookings(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || 'Failed to load owner bookings.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const matchesTab = booking.statusLabel === activeTab;
      const q = searchQuery.trim().toLowerCase();
      if (!q) return matchesTab;

      return matchesTab && [
        booking.client,
        booking.vehicleName,
        booking.id,
        booking.status
      ].filter(Boolean).some(value => String(value).toLowerCase().includes(q));
    });
  }, [bookings, activeTab, searchQuery]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Pending': return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'Confirmed': return 'bg-green-50 text-green-600 border-green-100';
      case 'Rejected': return 'bg-red-50 text-red-600 border-red-100';
      default: return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  };

  const toggleDropdown = (id) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  return (
    <div className="flex h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] overflow-hidden" onClick={() => setOpenDropdownId(null)}>
      <Sidebar activePage="Bookings" />

      <main className="flex-grow overflow-y-auto">
        <header className="bg-white border-b border-gray-100 flex items-center justify-between px-10 py-5 sticky top-0 z-20">
          <div className="relative w-96">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search bookings, clients, or vehicles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border-transparent rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-[#111111] focus:border-transparent transition-all outline-none"
            />
          </div>

          <div className="flex items-center gap-6">
            <button className="relative p-2 text-gray-400 hover:text-brand-black transition-all">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
            <div className="h-8 w-[1px] bg-gray-100 mx-2"></div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs font-black">{fullName}</p>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Pro Owner</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-brand-black text-white flex items-center justify-center font-black text-xs">
                {initials.toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        <div className="p-10">
          <div className="mb-4"><DashboardSwitcher active="/owner-dashboard"/></div>
          <OwnerTopNav activePage="Bookings"/>
          <div className="flex items-end justify-between mb-10">
            <div>
              <h1 className="text-4xl font-black tracking-tight mb-2 uppercase">Bookings</h1>
              <p className="text-gray-500 uppercase tracking-widest text-[10px] font-bold">Manage and review your incoming vehicle reservations.</p>
            </div>

            <div className="flex bg-gray-100 p-1.5 rounded-2xl gap-1">
              {['Pending', 'Confirmed', 'Rejected'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    activeTab === tab ? 'bg-white shadow-sm text-brand-black' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-sm border border-gray-100">
            {loading ? (
              <div className="py-32 flex flex-col items-center justify-center text-center">
                <Loader2 className="w-10 h-10 animate-spin text-gray-300 mb-5" />
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Loading bookings</p>
              </div>
            ) : error ? (
              <div className="py-32 flex flex-col items-center justify-center text-center px-6">
                <div className="bg-red-50 p-6 rounded-3xl mb-6">
                  <XCircle className="w-12 h-12 text-red-300" />
                </div>
                <h3 className="text-xl font-black uppercase mb-1">Unable to load bookings</h3>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{error}</p>
              </div>
            ) : (
              <>
                <div className="overflow-visible">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-50">
                        <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Client / ID</th>
                        <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Vehicle</th>
                        <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Dates</th>
                        <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Revenue</th>
                        <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                        <th className="px-10 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredBookings.map((booking) => {
                        const isDropdownOpen = openDropdownId === booking.id;

                        return (
                          <tr key={booking.id} className="hover:bg-gray-50/30 transition-all group">
                            <td className="px-10 py-8">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center border border-gray-100 text-brand-black">
                                  <User className="w-5 h-5 opacity-40" />
                                </div>
                                <div>
                                  <p className="text-sm font-black">{booking.client}</p>
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                                    {String(booking.id).slice(0, 8).toUpperCase()}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-10 py-8">
                              <div className="flex items-center gap-4">
                                <img src={buildImageUrl(booking.vehicleImageUrl)} alt="" className="w-12 h-12 rounded-xl object-cover shadow-sm" />
                                <div>
                                  <p className="text-sm font-black">{booking.vehicleName}</p>
                                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">{booking.vehicleYear || 'N/A'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-10 py-8">
                              <div>
                                <p className="text-sm font-black">{booking.startDate} - {booking.endDate}</p>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">{booking.days} Days</p>
                              </div>
                            </td>
                            <td className="px-10 py-8">
                              <p className="text-sm font-black text-brand-black">{formatCurrency(booking.revenue)}</p>
                              <p className="text-[10px] font-bold text-green-500 uppercase tracking-widest mt-0.5">Recorded</p>
                            </td>
                            <td className="px-10 py-8">
                              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[9px] font-black uppercase tracking-widest ${getStatusStyle(booking.statusLabel)}`}>
                                {booking.statusLabel === 'Pending' && <Clock className="w-3 h-3" />}
                                {booking.statusLabel === 'Confirmed' && <CheckCircle2 className="w-3 h-3" />}
                                {booking.statusLabel === 'Rejected' && <XCircle className="w-3 h-3" />}
                                {booking.statusLabel}
                              </div>
                              <p className="text-[9px] font-bold text-gray-400 mt-2 ml-1">{booking.requestTime}</p>
                            </td>
                            <td className="px-10 py-8 text-right relative">
                              <div className="flex items-center justify-end transition-opacity">
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDropdown(booking.id);
                                    }}
                                    className="flex items-center gap-2 bg-brand-black text-white px-5 py-2.5 rounded-xl text-[8px] font-black uppercase tracking-widest hover:bg-gray-800 transition-all shadow-lg shadow-gray-200"
                                  >
                                    Actions
                                    <ChevronRight className={`w-3 h-3 transition-transform ${isDropdownOpen ? 'rotate-90' : ''}`} />
                                  </button>

                                  {isDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-100 rounded-2xl shadow-2xl z-50 overflow-hidden py-1.5">
                                      {activeTab === 'Pending' ? (
                                        <>
                                          <button className="w-full text-left px-5 py-3 text-[8px] font-black uppercase tracking-widest text-green-600 hover:bg-green-50 transition-colors flex items-center gap-3">
                                            <CheckCircle2 className="w-4 h-4" />
                                            Approve
                                          </button>
                                          <button className="w-full text-left px-5 py-3 text-[8px] font-black uppercase tracking-widest text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3">
                                            <XCircle className="w-4 h-4" />
                                            Reject
                                          </button>
                                        </>
                                      ) : activeTab === 'Confirmed' ? (
                                        <>
                                          <button className="w-full text-left px-5 py-3 text-[8px] font-black uppercase tracking-widest text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3">
                                            <FileText className="w-4 h-4 text-gray-400" />
                                            View Details
                                          </button>
                                          <button className="w-full text-left px-5 py-3 text-[8px] font-black uppercase tracking-widest text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3">
                                            <Archive className="w-4 h-4 text-gray-400" />
                                            Download Invoice
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button className="w-full text-left px-5 py-3 text-[8px] font-black uppercase tracking-widest text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3">
                                            <FileText className="w-4 h-4 text-gray-400" />
                                            View Details
                                          </button>
                                          <button className="w-full text-left px-5 py-3 text-[8px] font-black uppercase tracking-widest text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3">
                                            <Archive className="w-4 h-4 text-gray-400" />
                                            Archive Booking
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {filteredBookings.length === 0 && (
                    <div className="py-32 flex flex-col items-center justify-center text-center">
                      <div className="bg-gray-50 p-6 rounded-3xl mb-6">
                        <CalendarDays className="w-12 h-12 text-gray-200" />
                      </div>
                      <h3 className="text-xl font-black uppercase mb-1">No {activeTab} Bookings</h3>
                      <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">Everything is up to date in this category.</p>
                    </div>
                  )}
                </div>

                <div className="px-10 py-6 border-t border-gray-50 flex items-center justify-between text-gray-400">
                  <p className="text-[10px] font-black uppercase tracking-widest">Showing {filteredBookings.length} results</p>
                  <div className="flex items-center gap-1">
                    <button className="p-2 hover:text-brand-black disabled:opacity-30" disabled>
                      <ChevronRight className="w-4 h-4 rotate-180" />
                    </button>
                    <div className="flex items-center gap-0.5">
                      <span className="w-8 h-8 rounded-lg bg-brand-black text-white flex items-center justify-center text-[10px] font-black">1</span>
                    </div>
                    <button className="p-2 hover:text-brand-black disabled:opacity-30" disabled>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default OwnerBookings;
